import { CSSProperties } from 'react';

export interface BaseStyledProps {
    className?: string;
    style?: CSSProperties;
}

export type HSV = {
    h: number;
    s: number;
    v: number;
};

export type RGB = {
    r: number;
    g: number;
    b: number;
};

export interface RGBA extends RGB {
    a: number;
}

export const intToHex = (num: number) => {
    return ('0' + Math.round(Number(num)).toString(16)).slice(-2).toUpperCase();
};

export const rgbToHex = ({ r, g, b }: RGB | RGBA): string => {
    return `#${intToHex(r)}${intToHex(g)}${intToHex(b)}`;
};

export const rgbaToCss = (color: RGBA): string => {
    return color.a === 1 ? rgbToHex(color) : `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a})`;
};

export const hexToRgb = (hex: string): RGB | null => {
    // By Tim Down - http://stackoverflow.com/a/5624139/3493650
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
        return r + r + g + g + b + b;
    });
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

    return result
        ? {
              r: parseInt(result[1], 16),
              g: parseInt(result[2], 16),
              b: parseInt(result[3], 16),
          }
        : null;
};

export const safeHexToRgb = (hex: string): RGB => {
    return hexToRgb(hex) || { r: 128, g: 128, b: 128 };
};

export const rgbToRgba = (color: RGB, alpha = 1): RGBA => {
    return { ...color, a: alpha };
};

export const rgbaToRgb = ({ r, g, b }: RGBA): RGB => {
    return { r, g, b };
};

export const hexToRgba = (hex: string, alpha = 1): RGBA => {
    return rgbToRgba(safeHexToRgb(hex), alpha);
};

export const hexToCss = (hex: string, alpha = 1): string => {
    return rgbaToCss(hexToRgba(hex, alpha));
};

export const hexToAlphaHex = (hex: string, alpha: number): string => {
    const alphaHex = Math.round(255 * alpha)
        .toString(16)
        .padEnd(2, '0');
    return (hex + alphaHex).toUpperCase();
};

export const alpha = (hex: string, alpha: number): string => {
    return hexToCss(hex, alpha);
};

export const hsvToRgb = (hsv: HSV): RGB => {
    const h = hsv.h / 60;
    const s = hsv.s / 100;
    let v = hsv.v / 100;
    const hi = Math.floor(h) % 6;

    const f = h - Math.floor(h);
    let p = 255 * v * (1 - s);
    let q = 255 * v * (1 - s * f);
    let t = 255 * v * (1 - s * (1 - f));
    v *= 255;

    v = Math.round(v);
    t = Math.round(t);
    p = Math.round(p);
    q = Math.round(q);

    let res: [number, number, number];
    switch (hi) {
        case 0:
            res = [v, t, p];
            break;
        case 1:
            res = [q, v, p];
            break;
        case 2:
            res = [p, v, t];
            break;
        case 3:
            res = [p, q, v];
            break;
        case 4:
            res = [t, p, v];
            break;
        case 5:
            res = [v, p, q];
            break;
        default:
            res = [0, 0, 0];
            break;
    }

    return { r: res[0], g: res[1], b: res[2] };
};

export function rgbToHsv(rgb: RGB): HSV {
    const r = rgb.r;
    const g = rgb.g;
    const b = rgb.b;
    const min = Math.min(r, g, b);
    const max = Math.max(r, g, b);
    const delta = max - min;
    let h: number;
    let s: number;

    if (max === 0) {
        s = 0;
    } else {
        s = (delta / max) * 100;
    }

    if (max === min) {
        h = 0;
    } else if (r === max) {
        h = (g - b) / delta;
    } else if (g === max) {
        h = 2 + (b - r) / delta;
    } else if (b === max) {
        h = 4 + (r - g) / delta;
    } else {
        h = 0;
    }

    h = Math.min(h * 60, 360);

    if (h < 0) {
        h += 360;
    }

    const v = ((max / 255) * 1000) / 10;

    return { h, s, v };
}

export const hsvToRgba = (hsv: HSV, alpha = 1): RGBA => {
    return rgbToRgba(hsvToRgb(hsv), alpha);
};

export const rgbaToHsv = (rgba: RGBA): HSV => {
    return rgbToHsv(rgbaToRgb(rgba));
};

export const darkenRGBA = ({ a, ...rgb }: RGBA, percent = 20): RGBA => {
    const hsv = rgbToHsv(rgb);
    return { ...hsvToRgb({ ...hsv, v: Math.max(0, hsv.v - percent) }), a };
};

export const lightenRGBA = ({ a, ...rgb }: RGBA, percent = 20): RGBA => {
    const hsv = rgbToHsv(rgb);
    return { ...hsvToRgb({ ...hsv, v: Math.min(hsv.v + percent, 100) }), a };
};

export const darkenHex = (hex: string, percent = 20): string => {
    return rgbToHex(darkenRGBA(hexToRgba(hex), percent));
};

export const lightenHex = (hex: string, percent = 20): string => {
    return rgbToHex(lightenRGBA(hexToRgba(hex), percent));
};
